import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import { useAuth } from '../../context/AuthContext';
import server from '../../global/server';
import { useApp } from '../../context/AppContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import useHandleErrors from '../../hooks/useHandleErrors';
import { useState } from 'react';
import {
    Box,
    TextField,
    Stack,
    Typography,
    Avatar,
    Chip,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SelectTopics from '../../components/SelectTopics';
import CityStateSelect from '../../components/CityStateSelect';

const SectionCard = ({ children, ...props }) => (
    <Box
        sx={{
            width: '100%',
            bgcolor: 'white',
            borderRadius: '16px',
            border: '1px solid #e8e8e8',
            p: { xs: '20px 16px', sm: '28px 24px' },
        }}
        {...props}
    >
        {children}
    </Box>
);

const SectionTitle = ({ children }) => (
    <Typography variant="subtitle1" fontWeight={600} color="#333" sx={{ mb: 2.5 }}>
        {children}
    </Typography>
);

export default () => {
    const { verifySession, user } = useAuth();
    const [search] = useSearchParams();
    const { isOffline } = useApp();
    const [hasError, setError] = useState();
    useHandleErrors(hasError);
    const navigation = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const isHostFlow = search.get('sign') === 'encontrado';

    const formik = useFormik({
        initialValues: {
            description: '',
            profession: '',
            education: '',
            residence: '',
            instagram: '',
            topics: [],
        },
        validationSchema: Yup.object().shape({
            description: Yup.string().required('Campo obrigatório').max(250, 'Máximo 250 caracteres'),
            residence: Yup.string().required('Selecione seu estado e cidade'),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);
                await server.put('/account', values);
                verifySession();
                setSubmitting(false);
                if (isHostFlow) {
                    navigation('/recebimento?sign=encontrado');
                    enqueueSnackbar('Perfil criado');
                } else {
                    enqueueSnackbar('Dados atualizados');
                }
            } catch (error) {
                setSubmitting(false);
                setError(error);
            }
        },
    });

    useEffect(() => {
        if (user) {
            formik.setValues({
                description: user.description || '',
                profession: user.profession || '',
                education: user.education || '',
                residence: user.residence || '',
                instagram: user.instagram || '',
                topics: user?.topics?.map(i => i.topic_id) || [],
            });
        }
    }, [user]);

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: 720,
            mx: 'auto',
            p: { xs: '16px 12px 32px', sm: '24px 16px 48px' },
            gap: 3,
        }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, pt: 1 }}>
                <Avatar
                    src={user?.profileImage}
                    alt={user?.name}
                    sx={{ width: 80, height: 80, border: '3px solid #e0e0e0', fontSize: '2rem' }}
                >
                    {user?.name?.[0]}
                </Avatar>
                <Box>
                    <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                        {isHostFlow ? 'Complete seu perfil de anfitrião' : 'Completando seu perfil'}
                    </Typography>
                    {isHostFlow && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Preencha seus dados profissionais para continuar
                        </Typography>
                    )}
                </Box>
                {isHostFlow && (
                    <Chip
                        icon={<PersonOutlineIcon sx={{ fontSize: 16 }} />}
                        label="Etapa 1 de 2"
                        size="small"
                        sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 500, '& .MuiChip-icon': { color: 'inherit' } }}
                    />
                )}
            </Box>

            {/* Formulario */}
            <SectionCard>
                <SectionTitle>Dados Profissionais</SectionTitle>
                <form onSubmit={formik.handleSubmit}>
                    <Stack spacing={2}>
                        <SelectTopics
                            onChange={formik.setFieldValue}
                            name="topics"
                            value={formik.values.topics}
                        />
                        <TextField
                            label="Profissão"
                            name="profession"
                            size="small"
                            fullWidth
                            value={formik.values.profession}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            disabled={isOffline}
                            error={Boolean(formik.errors.profession)}
                            helperText={formik.errors.profession}
                        />
                        <TextField
                            label="Formação"
                            name="education"
                            size="small"
                            fullWidth
                            value={formik.values.education}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            disabled={isOffline}
                            error={Boolean(formik.errors.education)}
                            helperText={formik.errors.education}
                        />
                        <CityStateSelect
                            value={formik.values.residence}
                            onChange={formik.setFieldValue}
                            name="residence"
                            disabled={isOffline}
                        />
                        <TextField
                            label="Instagram"
                            name="instagram"
                            size="small"
                            fullWidth
                            value={formik.values.instagram}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            disabled={isOffline}
                            error={Boolean(formik.errors.instagram)}
                            helperText={formik.errors.instagram}
                        />
                        <TextField
                            label="Conte sobre você e sua história"
                            name="description"
                            size="small"
                            fullWidth
                            multiline
                            minRows={3}
                            required
                            value={formik.values.description}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            disabled={isOffline}
                            error={Boolean(formik.errors.description)}
                            helperText={formik.errors.description || `${250 - (formik.values.description?.length || 0)} caracteres restantes`}
                            inputProps={{ maxLength: 250 }}
                        />

                        <LoadingButton
                            type="submit"
                            fullWidth
                            loading={formik.isSubmitting}
                            variant="contained"
                            disabled={isOffline}
                            sx={{
                                bgcolor: '#006a61',
                                borderRadius: '8px',
                                height: 44,
                                textTransform: 'none',
                                fontWeight: 500,
                                '&:hover': { bgcolor: '#005a52' },
                            }}
                        >
                            {isHostFlow ? 'Continuar' : 'Salvar perfil'}
                        </LoadingButton>

                        {!isHostFlow && (
                            <LoadingButton
                                onClick={() => navigation('/recebimento')}
                                fullWidth
                                variant="text"
                                sx={{ textTransform: 'none', color: '#006a61' }}
                            >
                                Dados de recebimento
                            </LoadingButton>
                        )}

                        {isHostFlow && (
                            <LoadingButton
                                onClick={() => navigation('/lista')}
                                fullWidth
                                variant="outlined"
                                sx={{
                                    borderRadius: '8px',
                                    height: 44,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    color: '#666',
                                    borderColor: '#ddd',
                                    '&:hover': { bgcolor: '#f5f5f5', borderColor: '#bbb' },
                                }}
                            >
                                Cancelar e continuar como conviva
                            </LoadingButton>
                        )}
                    </Stack>
                </form>
            </SectionCard>
        </Box>
    );
};
